import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

type SheetContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

const SheetContext = createContext<SheetContextValue | null>(null);

type SheetProps = {
  children: ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export function Sheet({ children, open, defaultOpen = false, onOpenChange }: SheetProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isOpen = open ?? internalOpen;

  const context = useMemo(
    () => ({
      open: isOpen,
      setOpen: (nextOpen: boolean) => {
        if (open === undefined) {
          setInternalOpen(nextOpen);
        }
        onOpenChange?.(nextOpen);
      }
    }),
    [isOpen, onOpenChange, open]
  );

  return <SheetContext.Provider value={context}>{children}</SheetContext.Provider>;
}

export function SheetTrigger({ className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const context = useContext(SheetContext);
  if (!context) {
    throw new Error('SheetTrigger must be used inside Sheet');
  }

  return (
    <button
      type="button"
      className={cn(
        'inline-flex h-10 items-center justify-center rounded-md border border-border bg-background/90 px-4 text-sm font-semibold text-foreground transition hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background',
        className
      )}
      onClick={() => context.setOpen(true)}
      {...props}
    />
  );
}

export function SheetContent({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const context = useContext(SheetContext);

  if (!context) {
    throw new Error('SheetContent must be used inside Sheet');
  }

  if (!context.open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/55" onClick={() => context.setOpen(false)} aria-hidden="true" />
      <aside
        className={cn(
          'absolute right-0 top-0 h-full w-full max-w-md overflow-y-auto border-l border-border bg-background p-6 shadow-elevated overscroll-contain',
          className
        )}
        {...props}
      >
        <div className="flex justify-end">
          <button
            type="button"
            className="rounded px-2 py-1 text-xs text-muted-foreground hover:bg-secondary"
            onClick={() => context.setOpen(false)}
          >
            Close
          </button>
        </div>
        {children}
      </aside>
    </div>
  );
}

export function SheetHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <header className={cn('space-y-1.5 text-left', className)} {...props} />;
}

export function SheetTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <h4 className={cn('text-lg font-semibold', className)} {...props} />;
}

export function SheetDescription({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('text-sm text-muted-foreground', className)} {...props} />;
}
